/**
 * @CODE:FIX-JP-SENTRY - Third-party Script Error Handler
 * Handles loading failures for OptiMonk and Judge.me on Mobile Safari
 *
 * Issues Resolved:
 * - SYRS-SHOPIFY-V, SYRS-SHOPIFY-Z (JP Mobile Safari iOS 18.5)
 * - SYRS-SHOPIFY-10, SYRS-SHOPIFY-S (KR module loading)
 * - SYRS-SHOPIFY-11 (KR Judge.me jQuery dependency)
 *
 * Root Causes:
 * - Network/CDN issues causing script loading failures
 * - Judge.me jQuery dependency not loaded
 *
 * Solutions:
 * - Error boundaries + fallback mechanisms + retry logic
 * - jQuery dependency check with graceful fallback
 *
 * Business Impact: Medium - Affects conversion optimization tools
 * User Impact: None (graceful degradation, no cart blockage)
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    retryAttempts: 2,
    retryDelay: 1000, // ms
    timeout: 10000, // 10 seconds
    logErrors: true
  };

  /**
   * Logs errors to console and Sentry (if available)
   * @param {string} source - Source of the error (OptiMonk, Judge.me, etc.)
   * @param {Error} error - The error object
   * @param {Object} context - Additional context
   */
  function logError(source, error, context = {}) {
    if (!CONFIG.logErrors) return;

    const errorMessage = `[Third-Party Loader] ${source} failed: ${error.message}`;
    console.warn(errorMessage, context);

    // Send to Sentry if available
    if (window.Sentry && typeof window.Sentry.captureException === 'function') {
      window.Sentry.captureException(error, {
        level: 'warning',
        tags: {
          error_source: 'third-party',
          third_party_app: source.toLowerCase(),
          handler: 'third-party-loader'
        },
        extra: context
      });
    }
  }

  /**
   * Creates a timeout promise that rejects after specified ms
   * @param {number} ms - Timeout in milliseconds
   * @returns {Promise}
   */
  function timeoutPromise(ms) {
    return new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`Timeout after ${ms}ms`)), ms);
    });
  }

  /**
   * Delays execution for specified ms
   * @param {number} ms - Delay in milliseconds
   * @returns {Promise}
   */
  function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Loads a script with retry logic and timeout
   * @param {string} src - Script URL
   * @param {Object} options - Loading options
   * @returns {Promise<boolean>} - Success status
   */
  async function loadScriptWithRetry(src, options = {}) {
    const {
      retries = CONFIG.retryAttempts,
      timeout = CONFIG.timeout,
      attributes = {}
    } = options;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // Add delay before retry attempts
        if (attempt > 0) {
          await delay(CONFIG.retryDelay * attempt);
        }

        await Promise.race([
          new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = src;
            script.async = true;

            // Add custom attributes
            Object.keys(attributes).forEach(key => {
              script.setAttribute(key, attributes[key]);
            });

            script.onload = () => resolve(true);
            script.onerror = () => reject(new Error(`Failed to load: ${src}`));

            document.head.appendChild(script);
          }),
          timeoutPromise(timeout)
        ]);

        return true; // Success
      } catch (error) {
        if (attempt === retries) {
          throw error; // Final attempt failed
        }
      }
    }

    return false;
  }

  /**
   * OptiMonk Error Handler
   * Wraps OptiMonk initialization with error boundary
   */
  function initOptiMonkHandler() {
    // Check if OptiMonk is configured
    const optimonkConfig = document.querySelector('[data-app-optimonk]');
    if (!optimonkConfig) {
      return; // Not configured, skip
    }

    try {
      // Create error boundary for OptiMonk global object
      const originalOptiMonk = window.OptiMonk || {};

      window.OptiMonk = new Proxy(originalOptiMonk, {
        get(target, prop) {
          try {
            return target[prop];
          } catch (error) {
            logError('OptiMonk', error, { property: prop });
            return undefined;
          }
        },
        set(target, prop, value) {
          try {
            target[prop] = value;
            return true;
          } catch (error) {
            logError('OptiMonk', error, { property: prop, value });
            return false;
          }
        }
      });

      // Monitor for loading errors
      window.addEventListener('error', function(event) {
        if (event.filename && event.filename.includes('optimonk')) {
          logError('OptiMonk', new Error(event.message), {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno
          });
          event.preventDefault(); // Prevent error from breaking page
        }
      }, true);

      // Monitor for unhandled promise rejections
      window.addEventListener('unhandledrejection', function(event) {
        if (event.reason && event.reason.message &&
            event.reason.message.toLowerCase().includes('optimonk')) {
          logError('OptiMonk', event.reason, {
            type: 'unhandledrejection'
          });
          event.preventDefault();
        }
      });

    } catch (error) {
      logError('OptiMonk', error, { phase: 'initialization' });
    }
  }

  /**
   * Judge.me Error Handler
   * Wraps Judge.me initialization with error boundary + jQuery dependency check
   */
  function initJudgeMeHandler() {
    try {
      // Create error boundary for Judge.me global objects
      const judgeMeObjects = ['jdgm', 'judgeme', 'JudgeMe'];

      judgeMeObjects.forEach(objName => {
        if (window[objName]) {
          const original = window[objName];
          window[objName] = new Proxy(original, {
            get(target, prop) {
              try {
                // Special handling for jQuery dependency ($)
                if (prop === '$') {
                  // Check if jQuery is available
                  if (typeof window.jQuery === 'undefined' && typeof window.$ === 'undefined') {
                    logError('Judge.me', new Error('jQuery dependency missing'), {
                      object: objName,
                      property: '$',
                      recommendation: 'Ensure jQuery loads before Judge.me'
                    });
                    // Return a safe no-op function to prevent crashes
                    return function() {
                      console.warn('[Judge.me] jQuery not available, operation skipped');
                      return { length: 0 }; // Return empty jQuery-like object
                    };
                  }
                  // jQuery available, return the real $ function
                  return window.jQuery || window.$;
                }
                return target[prop];
              } catch (error) {
                logError('Judge.me', error, { object: objName, property: prop });
                return undefined;
              }
            },
            apply(target, thisArg, args) {
              try {
                return target.apply(thisArg, args);
              } catch (error) {
                logError('Judge.me', error, { object: objName, args });
                return undefined;
              }
            }
          });
        }
      });

      // Monitor for loading errors
      window.addEventListener('error', function(event) {
        if (event.filename && (
          event.filename.includes('judge.me') ||
          event.filename.includes('judgeme')
        )) {
          logError('Judge.me', new Error(event.message), {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno
          });
          event.preventDefault();
        }
      }, true);

      // Monitor for unhandled promise rejections
      window.addEventListener('unhandledrejection', function(event) {
        if (event.reason && event.reason.message &&
            (event.reason.message.toLowerCase().includes('judge') ||
             event.reason.message.toLowerCase().includes('judgeme'))) {
          logError('Judge.me', event.reason, {
            type: 'unhandledrejection'
          });
          event.preventDefault();
        }
      });

    } catch (error) {
      logError('Judge.me', error, { phase: 'initialization' });
    }
  }

  /**
   * Generic module import error handler
   * Catches dynamic import() failures for third-party modules
   */
  function initModuleImportHandler() {
    // Wrap dynamic import if available
    if (typeof window.import === 'undefined') {
      return; // Browser doesn't support dynamic imports
    }

    window.addEventListener('unhandledrejection', function(event) {
      const reason = event.reason;

      // Check if this is a module import failure
      if (reason && reason.message &&
          (reason.message.includes('Failed to fetch dynamically imported module') ||
           reason.message.includes('Importing a module script failed'))) {

        // Extract module URL from error message
        const urlMatch = reason.message.match(/https?:\/\/[^\s]+/);
        const moduleUrl = urlMatch ? urlMatch[0] : 'unknown';

        // Determine source
        let source = 'Unknown';
        if (moduleUrl.includes('optimonk')) source = 'OptiMonk';
        else if (moduleUrl.includes('judge')) source = 'Judge.me';

        logError(source, reason, {
          type: 'module-import-failure',
          moduleUrl,
          userAgent: navigator.userAgent
        });

        event.preventDefault(); // Prevent error from showing in console
      }
    });
  }

  /**
   * Initialize all error handlers
   */
  function init() {
    try {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
        return;
      }

      // Initialize handlers
      initOptiMonkHandler();
      initJudgeMeHandler();
      initModuleImportHandler();

      // Log success
      if (CONFIG.logErrors) {
        console.log('[Third-Party Loader] Error handlers initialized');
      }
    } catch (error) {
      console.error('[Third-Party Loader] Failed to initialize:', error);
    }
  }

  // Auto-initialize
  init();

  // Export for testing
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      loadScriptWithRetry,
      logError,
      CONFIG
    };
  }
})();
